import { useNavigate } from 'react-router-dom';

import { Clock, MessageSquare } from 'lucide-react';

import type { BoardCard as Card, FlystStage } from '@/api/schema';
import { Avatar } from '@/components/ui/Avatar';
import { PointsMeter } from '@/features/candidates/components/PointsMeter';
import { StatusChip } from '@/features/candidates/components/StatusChip';
import { FLYST_LABEL, FLYST_OPTIONS } from '@/features/candidates/labels';
import { cx } from '@/lib/cx';
import type { DateFormat } from '@/lib/dates';

import styles from './BoardCard.module.css';

/**
 * One candidate on the board.
 *
 * Two different things to open, so two different targets: the card opens the screening
 * (what is happening with this person right now) and the name opens their record.
 * The name is a real link so it can be middle-clicked and copied; the card is a button
 * because it opens something in place.
 */
export function BoardCard({
  card,
  dateFormat,
  moving,
  dragging,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
}: {
  card: Card;
  dateFormat: DateFormat;
  /** True while this card's move is in flight. */
  moving: boolean;
  /** True while this card is the one being dragged. */
  dragging: boolean;
  onOpen: (card: Card) => void;
  onMove: (card: Card, stage: FlystStage) => void;
  onDragStart: (card: Card) => void;
  onDragEnd: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      className={cx(styles.card, moving && styles.moving, dragging && styles.dragging)}
      // Not while a move is in flight: the card is already somewhere it may not stay,
      // and dragging it again would queue a move from a stage it might not be in.
      draggable={!moving}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        // Some browsers refuse to start a drag with nothing on the transfer. The card
        // itself is passed through React state; the id here is only to satisfy that.
        event.dataTransfer.setData('text/plain', card.id);
        onDragStart(card);
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className={styles.body}
        onClick={() => {
          onOpen(card);
        }}
      >
        <Avatar name={card.full_name} size={30} />
        <span className={styles.who}>
          <span
            className={styles.name}
            role="link"
            tabIndex={0}
            onClick={(event) => {
              // Stops the card opening as well: two things are reachable from one
              // rectangle and a click has to mean exactly one of them.
              event.stopPropagation();
              void navigate(`/candidates/${card.id}`);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              void navigate(`/candidates/${card.id}`);
            }}
          >
            {card.full_name}
          </span>
          {/* Only when there is something to say. A zero on every card is noise. */}
          {card.note_count > 0 ? (
            <span
              className={styles.notes}
              title={`${card.note_count} note${card.note_count === 1 ? '' : 's'}`}
            >
              <MessageSquare size={11} aria-hidden="true" />
              {card.note_count}
            </span>
          ) : null}
          <span className={styles.position}>
            {card.preferred_position?.name ?? (
              <span className={styles.none}>No position yet</span>
            )}
          </span>
        </span>
      </button>

      <div className={styles.meta}>
        <PointsMeter value={card.sponsor_points} />
        <StatusChip status={card.status} history={{ candidateId: card.id, dateFormat }} />
      </div>

      <p className={styles.waiting}>
        <Clock size={12} aria-hidden="true" />
        {/* Days in this stage, not days since they applied: the number that says
            where the queue is stuck. */}
        {card.waiting_days === 0
          ? 'Moved here today'
          : `${card.waiting_days} ${card.waiting_days === 1 ? 'day' : 'days'} in this stage`}
      </p>

      <label className={styles.moveLabel}>
        <span className="visually-hidden">Move {card.full_name} to</span>
        <select
          className={styles.move}
          value={card.flyst_stage}
          disabled={moving}
          onChange={(event) => {
            onMove(card, event.target.value as FlystStage);
          }}
        >
          {FLYST_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {/* Every stage is offered, including the ones the API will refuse. The
                  rule is the server's, and a dropdown that quietly omits an option
                  teaches nothing about why it is not allowed. The refusal says. */}
              {option.value === card.flyst_stage
                ? FLYST_LABEL[option.value]
                : `Move to ${option.label}`}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
